import React from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { canAccessFeature, getUserRoleSlug } from "./authorization";
import PatientDashboard from "../pages/dashboards/patient/Dashboard";

const ProtectedRoute = () => {
  const token = localStorage.getItem("authToken");
  const storedUser = localStorage.getItem("user");
  const location = useLocation();

  if (!token) {
    return location.pathname === "/login" ? null : <Navigate to="/login" replace />;
  }

  const user = storedUser ? JSON.parse(storedUser) : null;

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <Outlet context={{ user }} />;
};

export default ProtectedRoute;

// =============================================================================
// ROUTE METADATA CONFIGURATION
// =============================================================================

export const ROUTE_META = {
  // Dashboard
  dashboard: {
    path: "/dashboard",
    meta: { feature: "pages.dashboard" },
  },
  "patient-dashboard": {
    path: "/patient-dashboard",
    meta: { feature: "pages.patient_dashboard" },
  },

  // My Tasks - Special handling for nurse_tasks
  "my-tasks": {
    path: "/my-tasks",
    meta: { feature: "pages.my_tasks", requireNurseTasks: false },
  },

  // Billing
  billing: {
    path: "/billings",
    meta: { feature: "pages.billing" },
  },
  "billing-audit-report": {
    path: "/billings/audit-report",
    meta: { feature: "pages.billing" },
  },
  "billing-share-report": {
    path: "/billings/share-audit-report",
    meta: { feature: "cross_account_access" },
  },
  "billing-report": {
    path: "/billings/billing-report",
    meta: { feature: "pages.billing_settings" },
  },
  "billing-settings": {
    path: "/billing-settings",
    meta: { feature: "pages.billing_settings" },
  },

  // Device Orders
  "device-orders": {
    path: "/device-orders",
    meta: { feature: "pages.device_orders" },
  },
  devices: {
    path: "/devices",
    meta: { feature: "pages.inventory" },
  },

  // Account Insights
  "account-insights": {
    path: "/account-insights",
    meta: { feature: "pages.account_insights" },
  },

  // User Settings
  "user-settings": {
    path: "/user-settings",
    meta: { feature: "pages.user_settings" },
  },

  // Admin pages
  admin: {
    path: "/admin",
    meta: { feature: "pages.admin" },
  },
  "admin-dashboard": {
    path: "/admin/dashboard",
    meta: { feature: "pages.admin" },
  },
  "admin-locations": {
    path: "/admin/locations/:id",
    meta: { feature: "pages.admin" },
  },
  "admin-instructional-videos": {
    path: "/admin/instructional-videos",
    meta: { feature: "pages.admin" },
  },
  "admin-add-organization": {
    path: "/admin/add-organization",
    meta: { feature: "cross_account_access" },
  },

  // Executive Dashboard (TOCA Super Admin only)
  "executive-dashboard": {
    path: "/executive-dashboard",
    meta: { feature: "pages.executive_dashboard" },
  },

  // Case Manager pages
  "case-manager": {
    path: "/case-manager",
    meta: { feature: "pages.case_manager" },
  },
  "case-manager-dashboard": {
    path: "/admin-case-manager",
    meta: { feature: "pages.case_manager" },
  },
  "reading-reviews": {
    path: "/case-manager/reading-reviews",
    meta: { feature: "pages.case_manager" },
  },
  "out-of-range": {
    path: "/case-manager/out-of-range",
    meta: { feature: "pages.case_manager" },
  },
  "create-patient": {
    path: "/case-manager/create-patient",
    meta: { feature: "pages.case_manager" },
  },
  "escalated-review": {
    path: "/case-manager/escalated-review",
    meta: { feature: "pages.case_manager" },
  },

  // Patient pages
  "patient-create": {
    path: "/patient/create-patient",
    meta: { feature: "pages.patient_detail" },
  },
  "patient-details": {
    path: "/patient/details",
    meta: { feature: "pages.patient_detail" },
  },
  "patient-care-plan": {
    path: "/patient/care-plan-pdf",
    meta: { feature: "pages.care_plan" },
  },
  "patient-monitor": {
    path: "/patient/monitor",
    meta: { feature: "pages.monitor" },
  },

  // Staff
  staff: {
    path: "/staff",
    meta: { feature: "pages.admin" },
  },

  // Inventory pages
  "inventory-dashboard": {
    path: "/inventory/dashboard",
    meta: { feature: "pages.inventory" },
  },
  "inventory-devices": {
    path: "/inventory/devices",
    meta: { feature: "pages.inventory" },
  },
  "inventory-supplies": {
    path: "/inventory/supplies",
    meta: { feature: "pages.inventory" },
  },
  "inventory-reports": {
    path: "/inventory/reports",
    meta: { feature: "pages.inventory" },
  },
  "inventory-orders": {
    path: "/inventory/orders",
    meta: { feature: "pages.inventory" },
  },
  "inventory-status": {
    path: "/inventory/status",
    meta: { feature: "pages.inventory" },
  },
  "inventory-warranty": {
    path: "/inventory/warranty",
    meta: { feature: "pages.inventory" },
  },
  "inventory-support": {
    path: "/inventory/support",
    meta: { feature: "pages.inventory" },
  },
  "inventory-usage": {
    path: "/inventory/usage",
    meta: { feature: "pages.inventory" },
  },
  "inventory-device-distribution": {
    path: "/inventory/device-distribution",
    meta: { feature: "pages.inventory" },
  },
  "inventory-device-search": {
    path: "/inventory/device-search",
    meta: { feature: "pages.inventory" },
  },

  // Office Inventory
  "office-inventory": {
    path: "/office-inventory",
    meta: { feature: "pages.inventory" },
  },

  // Help Documentation (public)
  "help-documentation": {
    path: "/help-documentation",
    meta: { requireAuth: false },
  },
};

export const getRouteMeta = (pathname) => {
  // Find matching route by comparing paths
  for (const [key, route] of Object.entries(ROUTE_META)) {
    if (pathname === route.path || pathname.startsWith(route.path + "/")) {
      return route.meta;
    }
  }
  return null;
};

export const shouldHideNavbar = (pathname) => {
  const hideForRoutes = [
    "/inventory",
    "/patient/create-patient",
    "/patient/dashboard",
    "/login",
    "/videos/watch",
  ];

  return hideForRoutes.some(route => pathname === route || pathname.startsWith(route + "/"));
};

export const shouldHideNavbarLinks = (pathname) => {
  const hideLinksForRoutes = [
    "/inventory",
    "/patient/dashboard",
  ];

  return hideLinksForRoutes.some(route => pathname === route || pathname.startsWith(route + "/"));
};

export const getAccessibleRoutes = (user) => {
  if (!user) return [];

  const roleSlug = getUserRoleSlug(user);
  if (!roleSlug) return [];

  const accessible = [];

  for (const [key, route] of Object.entries(ROUTE_META)) {
    // Skip routes that don't require auth
    if (route.meta?.requireAuth === false) {
      continue;
    }

    // Check if the route is accessible based on the role's feature access
    if (route.meta?.feature) {
      if (canAccessFeature(user, route.meta.feature)) {
        accessible.push(route.path);
      }
    }
  }

  return accessible;
};

